"use client"

import * as React from "react"
import { useEffect, useState } from "react"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

import { useDebounce } from "@/hooks/use-debounce"

interface Option {
  value: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

interface ComboboxInputProps {
  id: string
  value?: string
  onChange?: (value: string) => void
  options: Option[]
  onSearch?: (query: string) => void | Promise<void>
  isSearching?: boolean
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  size?: React.ComponentProps<typeof Button>["size"]
  error?: boolean
  triggerButton?: React.ReactNode
  contentProps?: React.ComponentPropsWithoutRef<typeof PopoverContent>
  renderOption?: (option: Option) => React.ReactNode
  emptyChildren?: React.ReactNode
  onCreateOption?: (label: string) => void
  createOptionLabel?: (label: string) => string
  className?: string
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
}

const SEARCH_DEBOUNCE_MS = 300

export function ComboboxInput({
  id,
  value,
  onChange,
  options = [],
  onSearch,
  isSearching = false,
  label,
  placeholder = "Select an option",
  searchPlaceholder,
  emptyText = "No results found.",
  disabled = false,
  size,
  error,
  triggerButton,
  contentProps,
  renderOption,
  emptyChildren,
  onCreateOption,
  createOptionLabel = (label) => `Use “${label}”`,
  className,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}: ComboboxInputProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [prevOptions, setPrevOptions] = useState<Option[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const selectedOption = options.find((option) => option.value === value)
  const { className: contentClassName, ...restContentProps } = contentProps || {}
  const [scrollViewport, setScrollViewport] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (options.length > 0) {
      setPrevOptions(options)
    }
  }, [options])

  useEffect(() => {
    setIsTyping(false)
  }, [options])

  useEffect(() => {
    if (!open || !onLoadMore || !scrollViewport) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollViewport
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      if (distanceFromBottom < 100 && hasMore && !isLoadingMore) {
        onLoadMore()
      }
    }

    scrollViewport.addEventListener("scroll", handleScroll)
    return () => scrollViewport.removeEventListener("scroll", handleScroll)
  }, [open, onLoadMore, hasMore, isLoadingMore, scrollViewport])

  useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore || !open || !scrollViewport) return

    const timer = setTimeout(() => {
      if (scrollViewport.scrollHeight <= scrollViewport.clientHeight) {
        onLoadMore()
      }
    }, 50)

    return () => clearTimeout(timer)
  }, [onLoadMore, hasMore, isLoadingMore, options.length, open, scrollViewport])

  const debouncedSearch = useDebounce((value: string) => {
    if (!onSearch) return
    if (value.trim().length > 0) {
      onSearch(value)
    } else {
      setIsTyping(false)
      onSearch("")
    }
  }, SEARCH_DEBOUNCE_MS)

  const handleInputValueChange = (newValue: string) => {
    setInputValue(newValue)

    if (!onSearch) {
      setIsTyping(false)
      return
    }

    if (newValue.trim()) {
      setIsTyping(true)
      debouncedSearch(newValue)
    } else {
      setIsTyping(false)
      onSearch("")
    }
  }

  return (
    <div className={cn("space-y-1", className)}>
      {label && (
        <Label htmlFor={id} className={cn(error && "text-destructive")}>
          {label}
        </Label>
      )}
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen)
          if (!isOpen) {
            if (onSearch && inputValue.trim()) onSearch("")
            setInputValue("")
            setIsTyping(false)
          }
        }}
      >
        <PopoverTrigger asChild>
          {triggerButton ? (
            triggerButton
          ) : (
            <Button
              id={id}
              type="button"
              variant="outline"
              size={size}
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className="w-full justify-between"
            >
              <span className="flex min-w-0 items-center gap-2">
                {selectedOption?.icon}
                <span className="truncate">{selectedOption?.label ?? placeholder}</span>
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className={cn("w-full p-0", contentClassName)} {...restContentProps}>
          <Command
            filter={
              onSearch
                ? () => 1
                : (value, search) => {
                    const option = options.find((opt) => opt.value === value)
                    if (!option) return 0

                    return option.label.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                  }
            }
          >
            <CommandInput
              placeholder={isSearching || isTyping ? "Searching..." : (searchPlaceholder ?? placeholder)}
              className="h-9"
              value={inputValue}
              onValueChange={handleInputValueChange}
              icon={isSearching || isTyping ? <Loader2 className="mr-2.5 h-4 w-4 animate-spin" /> : undefined}
            />
            <CommandList viewportRef={setScrollViewport}>
              {emptyChildren ? (
                emptyChildren
              ) : (
                <CommandEmpty>{!(isSearching || isTyping) && inputValue.trim() ? emptyText : null}</CommandEmpty>
              )}
              <CommandGroup>
                {onCreateOption &&
                  inputValue.trim() &&
                  !options.some((option) => option.label.toLowerCase() === inputValue.trim().toLowerCase()) && (
                    <CommandItem
                      value={`create-${inputValue.trim()}`}
                      onSelect={() => {
                        onCreateOption(inputValue.trim())
                        setInputValue("")
                        setIsTyping(false)
                        setOpen(false)
                      }}
                    >
                      {createOptionLabel(inputValue.trim())}
                    </CommandItem>
                  )}
                {((isSearching || isTyping) && options.length === 0 && prevOptions.length > 0
                  ? prevOptions
                  : options
                ).map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    onSelect={(currentValue) => {
                      onChange?.(currentValue)
                      setInputValue("")
                      setIsTyping(false)
                      setOpen(false)
                    }}
                  >
                    {renderOption ? (
                      renderOption(option)
                    ) : (
                      <>
                        {option.label}
                        <Check
                          className={cn("ml-auto h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")}
                        />
                      </>
                    )}
                  </CommandItem>
                ))}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
